import { Injectable } from '@angular/core';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { EstatePersisted } from '@app/dao/estate/estate.interface';
import { Observable, map, tap } from 'rxjs';
import {
  FiscalLawDto,
  RentDto,
} from './funnel/estate/funnel-bien-locatif.service';
import { InvestmentDto } from './funnel/estate/funnel-estate-shared.types';
import { LoanDto } from './funnel/funnel-loan.service';
import {
  OwnerEnum,
  OwnershipTypeEnum,
} from './funnel/funnel-ownership.service';

/**
 * Type de paiement SCPI
 */
export enum PaymentType {
  CASH = 'CASH',
  CREDIT = 'CREDIT',
}

/**
 * Informations sur le produit SCPI
 */
export interface ScpiProductDto {
  id: number;
  code?: string;
  label?: string;
  isin?: string;
  manager?: string;
  category?: string;
}

export interface ScpiDto extends InvestmentDto {
  id?: number;
  userId: number;
  accountId: number;
  typeUrl: 'scpi';
  productType: 'SCPI';

  // Informations sur le bien
  label: string;
  owner: OwnerEnum;
  ownershipType: OwnershipTypeEnum;
  currentOwnershipType?: OwnershipTypeEnum;
  dismemberKey?: number;
  dismemberDuration?: number;

  // Spécifique SCPI
  nbShare: number; // Nombre de parts (scpiNbShare)
  initialPartPrice: number; // Prix d'achat initial par part
  initialPartRevocationPrice?: number; // Prix de retrait initial
  currentPartPrice: number; // Prix actuel par part
  currentAmount: number; // Valorisation actuelle totale
  currentPartRevocationPrice?: number; // Prix de retrait actuel

  productId: number; // ID du produit SCPI
  product?: ScpiProductDto; // Détails du produit SCPI
  paymentType?: PaymentType; // Comptant ou crédit

  // Informations locatives
  rentalInvestmentId?: number;
  rentalType?: 'SCPI';

  // Loi fiscale
  fiscalLaw?: FiscalLawDto;

  // Loyers (dividendes) et prêts
  rents?: RentDto[]; // Dividendes versés
  loans?: LoanDto[]; // Si achat à crédit

  // Métadonnées
  realEstateType?: 'RENTAL';
  rentRatio?: number;
}

/**
 * Payload pour la création/modification d'une SCPI
 */
export interface ScpiPayload {
  id?: number;
  userId: number;
  accountId: number;
  typeUrl: 'scpi';
  productType: 'SCPI';
  label: string;
  owner: 'CLIENT';
  owners: Array<{ ownerId: number; share?: number }>;
  ownershipType: string;
  subscriptionSource?: string;
  initialAmount: number;
  lastValue: number;
  startDate: string;
  current: boolean;

  // Spécifique SCPI
  nbShare: number;
  initialPartPrice: number;
  initialPartRevocationPrice?: number;
  currentPartPrice: number;
  currentAmount: number;
  currentPartRevocationPrice?: number;
  productId: number;
  paymentType?: PaymentType;

  // Démembrement (nue-propriété / usufruit)
  dismemberDuration?: number;
  dismemberKey?: number;
  bareownership?: { [year: string]: number }; // Barème nue-propriété
  usufruct?: { [year: string]: number }; // Barème usufruit

  // Collections
  rents?: RentDto[];
  loans?: LoanDto[];
  fiscalLaw?: FiscalLawDto;
}

/**
 * Gestion des SCPI (Sociétés Civiles de Placement Immobilier)
 *
 * Responsabilités :
 * - Charger les SCPI depuis /realestate/scpi
 * - Sauvegarder (créer ou modifier) une SCPI
 * - Supprimer une SCPI
 * - Calculer les prix et montants SCPI basés sur l'historique de prix
 *
 * Architecture :
 * - Utilise l'API /realestate/scpi avec typeUrl = 'scpi'
 */
@Injectable({
  providedIn: 'root',
})
export class ScpiService {
  constructor(private estateDao: EstateDao) {}

  /**
   * Sauvegarde une SCPI (création ou modification)
   *
   * @param payload Données de la SCPI à sauvegarder
   * @param reset Si true, déclenche un reset des caches après sauvegarde
   * @returns Observable de la réponse API
   */
  saveScpi(
    payload: ScpiPayload,
    reset: boolean = true,
    onPersisted?: (persisted: EstatePersisted) => void,
  ): Observable<ScpiPayload> {
    return this.estateDao.editOrCreate(payload).pipe(
      tap(estate =>
        onPersisted?.({ id: estate.id, investmentId: estate.investmentId }),
      ),
      map(response => {
        if (reset) {
          this.estateDao.reset();
        }
        return response;
      }),
    );
  }

  /**
   * Charge toutes les SCPI de l'utilisateur
   *
   * @returns Observable de la liste des SCPI
   */
  loadScpis(): Observable<ScpiDto[]> {
    return this.estateDao.getScpisOnce().pipe(
      map(response => {
        return response.data?.list || [];
      }),
    );
  }

  /**
   * Supprime une SCPI
   *
   * @param userId ID de l'utilisateur
   * @param scpiId ID de la SCPI à supprimer
   * @returns Observable de la réponse de suppression
   */
  deleteScpi(userId: number, scpiId: number): Observable<void> {
    const entity = {
      id: scpiId,
      userId: userId,
      typeUrl: 'scpi',
    };
    return this.estateDao.deleteEstate(entity, false);
  }

  /**
   * Récupère le prix de la part SCPI à une date donnée
   *
   * Logique métier :
   * - Si la date d'achat est >= date d'un prix dans l'historique, prend le prix le plus récent avant cette date
   * - Si la date d'achat est < toutes les dates de l'historique, retourne null
   *
   * @param priceHistory Historique des prix de la SCPI (format: [{date: string, value: number}])
   * @param startDate Date d'achat au format ISO ou Date
   * @returns Le prix de la part à cette date, ou null si non disponible
   */
  getPartPriceAtDate(
    priceHistory: Array<{ date: string | Date; value: number }>,
    startDate: string | Date,
  ): number | null {
    if (!priceHistory || priceHistory.length === 0) return null;

    const targetDate = new Date(startDate);

    // Filtrer les prix <= date d'achat
    const validHistory = priceHistory.filter(
      history => new Date(history.date) <= targetDate,
    );

    let closestPrice: { date: string | Date; value: number } | undefined;

    if (validHistory.length === 0) {
      return null;
    } else {
      // Prix le plus récent avant la date d'achat
      closestPrice = validHistory.reduce((closest, current) => {
        return new Date(closest.date) > new Date(current.date)
          ? closest
          : current;
      });
    }

    return closestPrice?.value ?? null;
  }

  /**
   * Calcule le montant total investi (initialAmount)
   *
   * @param nbShare Nombre de parts
   * @param partPrice Prix d'une part
   * @returns Montant total arrondi à 2 décimales
   */
  calculateInitialAmount(nbShare: number, partPrice: number): number {
    return Math.round(nbShare * partPrice * 100) / 100;
  }

  /**
   * Calcule le nombre de parts qu'on peut acheter avec un montant
   *
   * @param initialAmount Montant total à investir
   * @param partPrice Prix d'une part
   * @returns Nombre de parts (arrondi à l'entier inférieur)
   */
  calculateNbShare(initialAmount: number, partPrice: number): number {
    if (partPrice === 0) return 0;
    return Math.floor(initialAmount / partPrice);
  }

  /**
   * Calcule le prix par part à partir du montant total et du nombre de parts
   *
   * @param initialAmount Montant total investi
   * @param nbShare Nombre de parts
   * @returns Prix par part arrondi à 2 décimales
   */
  calculatePartPrice(initialAmount: number, nbShare: number): number {
    if (nbShare === 0) return 0;
    return Math.round((initialAmount / nbShare) * 100) / 100;
  }

  /**
   * Récupère la date de prix la plus ancienne disponible
   *
   * Utile pour valider que la date d'achat n'est pas antérieure à l'historique
   *
   * @param priceHistory Historique des prix
   * @returns Date la plus ancienne ou null si l'historique est vide
   */
  getOldestPriceDate(
    priceHistory: Array<{ date: string | Date; value: number }>,
  ): Date | null {
    if (!priceHistory || priceHistory.length === 0) return null;

    const oldest = priceHistory.reduce((prev, current) => {
      return new Date(prev.date) < new Date(current.date) ? prev : current;
    });

    return new Date(oldest.date);
  }

  /**
   * Récupère la date de prix la plus récente disponible
   *
   * @param priceHistory Historique des prix
   * @returns Date la plus récente ou null si l'historique est vide
   */
  getMostRecentPriceDate(
    priceHistory: Array<{ date: string | Date; value: number }>,
  ): Date | null {
    if (!priceHistory || priceHistory.length === 0) return null;

    const mostRecent = priceHistory.reduce((prev, current) => {
      return new Date(prev.date) > new Date(current.date) ? prev : current;
    });

    return new Date(mostRecent.date);
  }

  /**
   * Convertit la clé de démembrement du format UI (0-100) vers le format backend (0-1)
   *
   *
   * @param uiValue Valeur UI (pourcentage 0-100)
   * @returns Valeur backend (décimal 0-1)
   */
  convertDismemberKeyForBackend(uiValue: number | null | undefined): number {
    if (uiValue == null || uiValue === 0) return 0;
    return Math.round((uiValue / 100) * 100) / 100;
  }

  /**
   * Convertit la clé de démembrement du format backend (0-1) vers le format UI (0-100)
   *
   * @param backendValue Valeur backend (décimal 0-1)
   * @returns Valeur UI (pourcentage 0-100)
   */
  convertDismemberKeyForUI(backendValue: number | null | undefined): number {
    if (backendValue == null || backendValue === 0) return 0;
    return Math.round(backendValue * 100 * 100) / 100;
  }
}
